import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import { connect } from "react-redux";
import swal from "sweetalert";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import MUIDataTable from "mui-datatables";
import { API_URL } from "../../config";
import axios from "axios";
import SaveIcon from "@material-ui/icons/Save";
import Button from "@material-ui/core/Button";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import SettingsBackupRestoreIcon from "@material-ui/icons/SettingsBackupRestore";
import CircularProgress from "@material-ui/core/CircularProgress";
import Snackbar from "@material-ui/core/Snackbar";
import Alert from "@material-ui/lab/Alert";
import { pathSpliter, checkAuthBranchWare, sortRecordsNewestFirst } from "../../lib/functions";
import {
  currentRouteSet,
  createdDeliveryNameSet,
  updatedDeliveryNameSet,
  disableRestoreDeliveryNameSet,
} from "../../actions/actions";

const DeliveryManManage = ({
  location,
  currentRouteSet,
  authInfo,
  createdDelivery,
  updatedDelivery,
  deliveryDisableRestore,
  createdDeliveryNameSet,
  updatedDeliveryNameSet,
  disableRestoreDeliveryNameSet,
}) => {
  const classes = useStyles();
  const [formValues, setFormValues] = useState({
    action: "create",
    delivery_name: "",
    delivery_mobile: "",
    delivery_nid: "",
    delivery_name_id: null,
  });
  const [loadingSave, setLoadingSave] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [deliveryman, setDeliveryman] = useState([]);
  const [deliveryUpdateIndex, setDeliveryUpdateIndex] = useState(-1);

  const nameRef = useRef()
  const phoneRef = useRef()
  const nidRef = useRef()

  const [successMsg, setSuccessMsg] = useState({
    open: false,
    vertical: "top",
    horizontal: "center",
    msg: "",
  });

  const upsertDeliveryRow = (prevRows, incomingRow, { moveToTop = false } = {}) => {
    if (!incomingRow) return prevRows;

    const nextRow = {
      delivery_name_id:
        incomingRow.delivery_name_id ?? formValues.delivery_name_id ?? Date.now(),
      delivery_name_status: incomingRow.delivery_name_status ?? "active",
      delivery_name: incomingRow.delivery_name ?? formValues.delivery_name.trim(),
      delivery_mobile: incomingRow.delivery_mobile ?? formValues.delivery_mobile.trim(),
      delivery_nid: incomingRow.delivery_nid ?? formValues.delivery_nid.trim(),
    };

    const filteredRows = prevRows.filter(
      (item) =>
        String(item.delivery_name_id) !== String(nextRow.delivery_name_id)
    );

    return moveToTop
      ? [nextRow, ...filteredRows]
      : sortRecordsNewestFirst([nextRow, ...filteredRows]);
  };

  const { vertical, horizontal, open, msg } = successMsg;
  const handleClose = () => {
    setSuccessMsg({ ...successMsg, open: false });
  };

  useEffect(() => {
    currentRouteSet(pathSpliter(location.pathname, 1));
    getDeliveryman();
  }, []);

  const getDeliveryman = async () => {
    setLoadingList(true);
    try {
      const res = await axios.post(`${API_URL}/api/get-delivery-names`, null, {
        headers: { "auth-token": authInfo.token },
      });
      setDeliveryman(sortRecordsNewestFirst(res.data.message || []));
    } catch (error) {
      console.error("Error fetching delivery men:", error);
      swal({
        title: "Error loading delivery men",
        text: error.message,
        icon: "error",
      });
    } finally {
      setLoadingList(false);
    }
  };

  // Handle real-time updates
  useEffect(() => {
    if (createdDelivery) {
      if (checkAuthBranchWare(createdDelivery.user_branch_id)) {
        setDeliveryman((prev) =>
          upsertDeliveryRow(prev, createdDelivery.createdRow?.[0], { moveToTop: true })
        );
        setSuccessMsg({
          ...successMsg,
          msg: createdDelivery.msg,
          open: true,
        });
        resetForm();
      }
    }
  }, [createdDelivery]);

  useEffect(() => {
    if (updatedDelivery) {
      if (checkAuthBranchWare(updatedDelivery.user_branch_id)) {
        setDeliveryman((prev) => upsertDeliveryRow(prev, updatedDelivery.updatedRow?.[0]));
        setSuccessMsg({
          ...successMsg,
          msg: updatedDelivery.msg,
          open: true,
        });
        resetForm();
      }
    }
  }, [updatedDelivery]);

  useEffect(() => {
    if (deliveryDisableRestore) {
      if (checkAuthBranchWare(deliveryDisableRestore.user_branch_id)) {
        const disabledRow = deliveryDisableRestore.disableRestoreRow?.[0];
        setDeliveryman((prev) => {
          if (disabledRow?.delivery_name_status === "active") {
            return upsertDeliveryRow(prev, disabledRow);
          }

          return prev.filter(
            (item) =>
              String(item.delivery_name_id) !==
              String(disabledRow?.delivery_name_id)
          );
        });
        setSuccessMsg({
          ...successMsg,
          msg: deliveryDisableRestore.msg,
          open: true,
        });
      }
    }
  }, [deliveryDisableRestore]);

  const resetForm = () => {
    setFormValues({
      action: "create",
      delivery_name: "",
      delivery_mobile: "",
      delivery_nid: "",
      delivery_name_id: null,
    });
    setDeliveryUpdateIndex(-1);
  };

  const handleFromInput = (e) => {
    const { name, value } = e.target;
    setFormValues(prev => ({ ...prev, [name]: value }));
  };

  const saveFormAction = async () => {
    if (!formValues.delivery_name.trim()) {
      swal({
        title: "Delivery man name is required",
        icon: "warning",
      });
      return;
    }

    if (formValues.delivery_mobile && formValues.delivery_mobile.length !== 11) {
      swal({
        title: "Mobile number must be 11 digits",
        icon: "warning",
      });
      return;
    }

    const payload = {
      ...formValues,
      deliveryNameUpdateIndex: deliveryUpdateIndex,
    };

    setLoadingSave(true);
    try {
      const res = await axios.post(`${API_URL}/api/delivery-name-cu`, payload, {
        headers: { "auth-token": authInfo.token },
      });

      if (res.data.error) {
        swal({
          title: res.data.message,
          icon: "warning",
        });
        return;
      }

      const responseRow =
        res?.data?.row ||
        res?.data?.createdRow?.[0] ||
        res?.data?.updatedRow?.[0] ||
        (Array.isArray(res?.data?.message) ? res.data.message[0] : null);

      if (formValues.action === "create") {
        setDeliveryman((prev) =>
          upsertDeliveryRow(prev, responseRow, { moveToTop: true })
        );
      } else {
        setDeliveryman((prev) => upsertDeliveryRow(prev, responseRow));
      }

      await getDeliveryman();

      setSuccessMsg({
        ...successMsg,
        msg:
          typeof res?.data?.message === "string"
            ? res.data.message
            : formValues.action === "create"
              ? "Delivery man added successfully"
              : "Delivery man updated successfully",
        open: true,
      });
      resetForm();
    } catch (error) {
      console.error("Error saving delivery man:", error);
      swal({
        title: "Error saving delivery man",
        text: error.message,
        icon: "error",
      });
    } finally {
      setLoadingSave(false);
    }

    if (nameRef.current) {
      nameRef.current.focus()
    }
  };

  const deliveryEdit = (rowData, index) => {
    const delivery = deliveryman[index];
    setFormValues({
      action: "update",
      delivery_name_id: delivery.delivery_name_id,
      delivery_name: delivery.delivery_name,
      delivery_mobile: delivery.delivery_mobile,
      delivery_nid: delivery.delivery_nid,
    });
    setDeliveryUpdateIndex(index);
  };

  const handleDisableRestore = async (deliveryId, action, index) => {
    if (action === "disable") {
      const willContinue = await swal({
        title: "Are you sure?",
        text: "Do you want to delete this delivery man?",
        icon: "warning",
        buttons: ["No", "Yes"],
        dangerMode: true,
      });
      if (!willContinue) return;
    }

    try {
      const res = await axios.post(
        `${API_URL}/api/delivery-name-disable-restore`,
        {
          delivery_id: deliveryId,
          delivery_name_id: deliveryId,
          action,
          actionAlias: action === "disable" ? "d" : "a",
          index,
        },
        { headers: { "auth-token": authInfo.token } }
      );
      if (res?.data?.error) {
        swal({ title: res.data.message || "Failed to update delivery man", icon: "warning" });
        return false;
      }

      if (action === "disable") {
        setDeliveryman((prev) =>
          prev.filter(
            (item) => String(item.delivery_name_id) !== String(deliveryId)
          )
        );
      } else {
        setDeliveryman((prev) =>
          prev.map((item) =>
            String(item.delivery_name_id) === String(deliveryId)
              ? { ...item, delivery_name_status: "active" }
              : item
          )
        );
      }

      await getDeliveryman();

      setSuccessMsg({
        ...successMsg,
        msg:
          typeof res?.data?.message === "string"
            ? res.data.message
            : action === "disable"
              ? "Delivery man deleted successfully"
              : "Delivery man restored successfully",
        open: true,
      });
    } catch (error) {
      console.error("Error updating status:", error);
      swal({
        title: "Error updating delivery man",
        text: error.message,
        icon: "error",
      });
    }
  };

  const ActionOptions = ({ rowData, rowIndex }) => {
    return (
      <div style={{ textAlign: "right" }}>
        {authInfo.role !== "user" && (
          <>
            <EditIcon
              style={{
                cursor: "pointer",
                fontSize: "28px",
                color: "rgb(15, 126, 119)",
              }}
              onClick={() => deliveryEdit(rowData, rowIndex)}
            />
            {rowData[1] === "active" ? (
              <DeleteIcon
                style={{
                  cursor: "pointer",
                  color: "#ff0202",
                  fontSize: "28px",
                }}
                onClick={() => handleDisableRestore(rowData[0], "disable", rowIndex)}
              />
            ) : (
              <SettingsBackupRestoreIcon
                style={{
                  cursor: "pointer",
                  color: "rgb(60, 178, 194)",
                  fontSize: "28px",
                }}
                onClick={() => handleDisableRestore(rowData[0], "restore", rowIndex)}
              />
            )}
          </>
        )}
      </div>
    );
  };

  const columns = [
    { name: "delivery_name_id", options: { display: "excluded" } },
    { name: "delivery_name_status", options: { display: "excluded" } },
    {
      name: "SL",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, tableMeta) => {
          return tableMeta.rowIndex + 1;
        },
      },
    },
    {
      name: "delivery_name",
      label: "Delivery Name",
      options: { filter: true, sort: true },
    },
    {
      name: "delivery_mobile",
      label: "Mobile No",
      options: { filter: true, sort: true },
    },
    {
      name: "delivery_nid",
      label: "NID",
      options: { filter: true, sort: true },
    },
    {
      name: "delivery_status",
      label: "Status",
      options: {
        filter: true,
        sort: true,
        display: false,
      },
    },
    {
      name: "actions",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, tableMeta) => (
          <ActionOptions rowData={tableMeta.rowData} rowIndex={tableMeta.rowIndex} />
        ),
      },
    },
  ];

  const options = {
    filterType: "checkbox",
    selectableRows: "none",
    print: false,
    download: false,
  };

  return (
    <div className={classes.root}>
      <Snackbar open={open} autoHideDuration={6000} onClose={handleClose}>
        <Alert onClose={handleClose} severity="success">
          {msg}
        </Alert>
      </Snackbar>

      <Paper className={classes.paper}>
        <h2 className={classes.pageEntryLabel}>Delivery Man Management</h2>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              inputRef={nameRef}
              fullWidth
              variant="outlined"
              size="small"
              label="Delivery Name"
              name="delivery_name"
              value={formValues.delivery_name}
              onChange={handleFromInput}
              onKeyDown={(e) => {
                if (e.key == "Enter") {
                  if (phoneRef.current) {
                    phoneRef.current.focus()
                  }
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              inputRef={phoneRef}
              fullWidth
              variant="outlined"
              size="small"
              label="Mobile Number"
              name="delivery_mobile"
              value={formValues.delivery_mobile}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d{0,11}$/.test(value)) {
                  handleFromInput(e);
                }
              }}
              onKeyDown={(e) => {
                if (e.key == "Enter") {
                  if (nidRef.current) {
                    nidRef.current.focus()
                  }
                }
              }}
              error={formValues.delivery_mobile.length > 0 && formValues.delivery_mobile.length !== 11}
              helperText={
                formValues.delivery_mobile.length > 0 && formValues.delivery_mobile.length !== 11
                  ? "Must be 11 digits"
                  : ""
              }
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              inputRef={nidRef}
              fullWidth
              variant="outlined"
              size="small"
              label="NID Number"
              name="delivery_nid"
              value={formValues.delivery_nid}
              onChange={handleFromInput}
              onKeyDown={(e) => {
                if (e.key == "Enter") {
                  saveFormAction()
                }
              }}
            />
          </Grid>
        </Grid>

        <div style={{ marginTop: 20, textAlign: "center" }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            disabled={loadingSave}
            onClick={saveFormAction}
          >
            {loadingSave ? <CircularProgress size={24} /> : "Save"}
          </Button>
        </div>
      </Paper>

      <Paper className={classes.paper} style={{ marginTop: 20 }}>
        {loadingList ? (
          <div style={{ textAlign: "center", padding: 20 }}>
            <CircularProgress />
          </div>
        ) : (
          <MUIDataTable
            title="Delivery Men List"
            data={deliveryman}
            columns={columns}
            options={options}
          />
        )}
      </Paper>
    </div>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  pageEntryLabel: {
    color: "#3f51b5",
    marginBottom: theme.spacing(2),
  },
}));

const mapStateToProps = (state) => ({
  currentRoute: state.currentRouteReducer,
  authInfo: state.authInfoReducer,
  createdDelivery: state.createdDeliveryNameReducer,
  updatedDelivery: state.updatedDeliveryNameReducer,
  deliveryDisableRestore: state.deliveryNameDisableRestoreReducer,
});

export default connect(mapStateToProps, {
  currentRouteSet,
  createdDeliveryNameSet,
  updatedDeliveryNameSet,
  disableRestoreDeliveryNameSet,
})(DeliveryManManage);
